'use client';

/**
 * Tiny React context that lifts the current multi-selection count
 * (nodes + edges) out of `WorkspaceCanvas` so per-item toolbars
 * (AnnotationToolbar / ConnectorEdgeToolbar) can early-return null
 * when the user is editing the canvas as a group.
 *
 * Why not put this on the workspace store: the selection lives in
 * `useState` inside `WorkspaceCanvas` (it has React-Flow-specific
 * change semantics), not in the persistent workspace store. A context
 * scoped to the canvas keeps that locality without poking a temporary
 * UI concern into the store's typed surface.
 */

import { createContext, useContext } from 'react';

export interface WorkspaceSelection {
  /** Total selected nodes + edges. Used to gate per-item floating
   *  toolbars and the unified `SelectionToolbar`. */
  count: number;
  /** Live id sets. Mutating these is forbidden — they're shared by
   *  reference with the controlled RF state in WorkspaceCanvas. */
  nodeIds: ReadonlySet<string>;
  edgeIds: ReadonlySet<string>;
}

const SelectionContext = createContext<WorkspaceSelection>({
  count: 0,
  nodeIds: new Set<string>(),
  edgeIds: new Set<string>(),
});

export const WorkspaceSelectionProvider = SelectionContext.Provider;

export function useWorkspaceSelection(): WorkspaceSelection {
  return useContext(SelectionContext);
}
